/**
 * Состояния экрана: загрузка, пустота, ошибка.
 *
 * Требование 17.13: у каждого асинхронного экрана есть все три. Скелетон
 * вместо спиннера — экран должен «проявляться», а не мигать пустотой.
 */

import clsx from 'clsx'
import type { ReactNode } from 'react'

import { ApiError } from '@/shared/api/client'
import { Button } from '@/shared/ui/Button'
import { Icon, type IconName } from '@/shared/ui/Icon'
import { Card } from '@/shared/ui/primitives'

export function Skeleton({ className }: { className?: string }) {
  return <div className={clsx('shimmer rounded-[var(--radius-thumb)]', className)} aria-hidden />
}

export function SkeletonCard({ lines = 3 }: { lines?: number }) {
  return (
    <Card className="space-y-3">
      <Skeleton className="h-4 w-1/3" />
      {Array.from({ length: lines }).map((_, index) => (
        <Skeleton key={index} className={clsx('h-3', index === lines - 1 ? 'w-2/3' : 'w-full')} />
      ))}
    </Card>
  )
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon: IconName
  title: string
  description?: string
  action?: ReactNode
}) {
  return (
    <div className="enter flex flex-col items-center px-6 py-12 text-center">
      <div
        aria-hidden
        className="bg-accent-soft border-line/60 pop mb-4 flex size-[68px] rotate-[-4deg] items-center justify-center rounded-[var(--radius-card)] border shadow-[var(--shadow-card)]"
      >
        <Icon name={icon} size={34} />
      </div>
      <h3 className="font-display text-[21px] leading-tight">{title}</h3>
      {description && (
        <p className="text-text-muted mt-2 max-w-[280px] text-note leading-snug">
          {description}
        </p>
      )}
      {action && <div className="mt-5">{action}</div>}
    </div>
  )
}

/** Человеческий текст для кода ошибки — вместо «Request failed with 409». */
function humanize(error: unknown): { title: string; description: string } {
  if (error instanceof ApiError) {
    if (error.isAuth) {
      return {
        title: 'Сессия устарела',
        description: 'Закройте и откройте приложение из Telegram — этого достаточно.',
      }
    }
    if (error.code === 'network_error') {
      return {
        title: 'Нет связи',
        description: 'Проверьте интернет. Данные не потеряются — отправим, когда сеть вернётся.',
      }
    }
    if (error.status === 403) {
      return { title: 'Нет доступа', description: error.message }
    }
    return { title: 'Не получилось', description: error.message }
  }
  return {
    title: 'Что-то пошло не так',
    description: 'Мы уже смотрим. Попробуйте ещё раз через минуту.',
  }
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { title, description } = humanize(error)
  return (
    <div className="enter flex flex-col items-center px-6 py-12 text-center">
      <div
        aria-hidden
        className="bg-warm-soft border-line/60 pop mb-4 flex size-[68px] rotate-[3deg] items-center justify-center rounded-[var(--radius-card)] border shadow-[var(--shadow-card)]"
      >
        <Icon name="state-error" size={34} />
      </div>
      <h3 className="font-display text-title leading-tight">{title}</h3>
      <p className="text-text-muted mt-2 max-w-[280px] text-note leading-snug">{description}</p>
      {onRetry && (
        <Button className="mt-5" variant="secondary" onClick={onRetry}>
          Попробовать снова
        </Button>
      )}
    </div>
  )
}

/** Полоса поверх контента: что-то не отправилось, но экран продолжает жить. */
export function ErrorBanner({
  message,
  onRetry,
  onDismiss,
}: {
  message: string
  onRetry?: () => void
  onDismiss?: () => void
}) {
  return (
    <div
      role="alert"
      className="bg-warm-soft text-warm-ink flex items-center gap-3 rounded-[var(--radius-button)] px-4 py-3 text-note"
    >
      <Icon name="state-warning" size={18} />
      <span className="min-w-0 flex-1">{message}</span>
      {onRetry && (
        <button type="button" onClick={onRetry} className="tap-target font-semibold underline">
          Повторить
        </button>
      )}
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Скрыть"
          className="tap-target flex items-center justify-center opacity-60"
        >
          <Icon name="action-close" size={16} />
        </button>
      )}
    </div>
  )
}
